import { sb } from '../supabase';

// Invoice data layer for a direct order. The invoice is generated BY Maabar on
// behalf of the supplier: the supplier fills the line items + trade details, the
// server (save_order_invoice) computes the goods subtotal, the visible 5% Maabar
// fee and the total; issue_order_invoice locks it and reveals it to the buyer.

export async function fetchOrderInvoice(requestId) {
  const { data } = await sb.from('order_invoices').select('*').eq('request_id', requestId).maybeSingle();
  return data || null;
}

export async function saveOrderInvoice(requestId, { lineItems, incoterms, port, hsCode, notes, currency }) {
  const { data, error } = await sb.rpc('save_order_invoice', {
    p_request_id: requestId,
    p_line_items: lineItems,
    p_incoterms: incoterms || null,
    p_port: port || null,
    p_hs_code: hsCode || null,
    p_notes: notes || null,
    p_currency: currency || 'SAR',
  });
  if (error) throw error;
  return data;
}

export async function issueOrderInvoice(requestId) {
  const { data, error } = await sb.rpc('issue_order_invoice', { p_request_id: requestId });
  if (error) throw error;
  return data;
}

// Chat-agreement invoice: a supplier issues an invoice to a trader for one of
// their products (agreed in chat). Returns the created invoice (status 'issued').
export async function issueChatInvoice({ buyerId, productRef, quantity, unitPrice, incoterms, port, hsCode, notes, currency, lineItems, specs, requestId }) {
  const { data, error } = await sb.rpc('issue_chat_invoice', {
    p_buyer_id: buyerId, p_product_ref: productRef, p_quantity: Number(quantity) || 1, p_unit_price: Number(unitPrice) || 0,
    p_incoterms: incoterms || null, p_port: port || null, p_hs_code: hsCode || null, p_notes: notes || null, p_currency: currency || 'USD',
    p_line_items: lineItems || null, p_specs: specs || null, p_request_id: requestId || null,
  });
  if (error) throw error;
  return data;
}

export async function fetchInvoiceById(id) {
  const { data } = await sb.from('order_invoices').select('*').eq('id', id).maybeSingle();
  return data || null;
}

// Payments recorded against an order (for the invoice's deposit/balance schedule).
// RLS returns only rows the caller may see (their own as buyer or supplier).
export async function fetchInvoicePayments(requestId) {
  if (!requestId) return [];
  const { data } = await sb.from('payments')
    .select('status, amount, payment_pct, amount_first, amount_second')
    .eq('request_id', requestId).order('created_at', { ascending: true });
  return data || [];
}

// The supplier's own products (for the chat-invoice product picker). Includes
// inactive/unpublished ones — a supplier can invoice a product they agreed on in
// chat even before their account is verified/the product is published.
export async function fetchMyProductsForInvoice() {
  const { data: { user } } = await sb.auth.getUser();
  if (!user) return [];
  // Products have no scalar price column (pricing is tiered) — the supplier types
  // the agreed price in the composer, so we only need name + currency here.
  const { data } = await sb.from('products')
    .select('id, name_ar, name_en, name_zh, currency')
    .eq('supplier_id', user.id).order('created_at', { ascending: false });
  return data || [];
}

// A sensible default first line item from the order/product.
export function defaultLineItem(order) {
  const qty = Number(order?.quantity) || 1;
  const unit = Number(order?.unit_price ?? order?.price ?? 0) || 0;
  const desc = order?.product_name || order?.title_ar || order?.title_en || '';
  return [{ desc, qty, unit_price: unit, amount: qty * unit }];
}
